<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :disabled="isLoading || !isPhoneNumberValid || !isEmailValid"
    loading-message="Creating a Grocery..."
    @submit="submitForm()"
    @reset="resetForm"
  >
    <template #title>
      Create Grocery Info
    </template>

    <template #card-notes>
      <div class="card-notes notification-message pt-1">
        Please create a grocery after you place a order in IC account.<br>
        Please double check phone number, IC account email and order ID are matched.
      </div>
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Saved Grocery Lists</label>
        <div class="col-sm-7">
          <sanitized-input
            id="groceryName"
            v-model="groceryInfo.name"
            :required="groceryInfo.noGoGoFee || groceryInfo.doNotCharge"
            placeholder="Saved Grocery Lists"
            :disabled="isDisableName"
          />
        </div>
      </div>
      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Phone Number*</label>
        <phone-number-input
          id="customer-phone"
          v-model="groceryInfo.phoneNumber"
          class="col-sm-7"
          :page-id="pageId"
          disabled
        />
      </div>
      <div class="row align-items-center">
        <label class="col-sm-4 control-label">Grocery Order Id*</label>
        <div class="col-sm-7">
          <sanitized-input
            id="orderId"
            v-model="groceryInfo.orderId"
            placeholder="Grocery Order Id"
            required
          />
        </div>
      </div>
      <div class="row">
        <label class="col-sm-4 control-label pt-10">IC Account Email*</label>
        <div class="col-sm-7">
          <email-input
            v-model="groceryInfo.email"
            placeholder="IC Account Email"
            required
            @update-validation="updateEmailValidation"
          />
        </div>
      </div>
      <div class="row align-items-center">
        <div class="col-sm-8 offset-4">
          <el-checkbox
            id="conditionalTip"
            v-model="groceryInfo.conditionalTip"
          >
            Conditional tip
          </el-checkbox>
        </div>
      </div>
      <div class="row align-items-center">
        <div class="col-sm-8 offset-4">
          <el-checkbox
            id="doNotCharge"
            v-model="groceryInfo.doNotCharge"
          >
            Do not charge Grocery
          </el-checkbox>
        </div>
      </div>
      <div class="row align-items-center">
        <div class="col-sm-8 offset-4">
          <el-checkbox
            id="noGoGoFee"
            v-model="groceryInfo.noGoGoFee"
          >
            Do not charge GoGo Fee
          </el-checkbox>
        </div>
      </div>
      <div class="row align-items-center">
        <div class="col-sm-8 offset-4">
          <el-checkbox
            id="applyDiscountedFee"
            v-model="groceryInfo.applyDiscountedFee"
          >
            Apply discounted GoGo Fee
          </el-checkbox>
        </div>
      </div>
      <fieldset v-if="groceryInfo.doNotCharge">
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Did IC provided credits?</label>
          <div class="col-sm-7">
            <dropdown-list
              v-model="groceryInfo.isInstacartProvidedCredits"
              required
              :options="yesNoOptions"
            />
          </div>
        </div>
        <div
          v-if="groceryInfo.isInstacartProvidedCredits === 'Yes'"
          class="row align-items-center"
        >
          <div class="offset-sm-4 col-sm-7">
            <dropdown-list
              v-model="groceryInfo.doNotChargeReason"
              :options="doNotChargeReasonList"
            />
          </div>
        </div>
        <div
          v-if="groceryInfo.isInstacartProvidedCredits === 'No'"
          class="row align-items-center"
        >
          <div class="offset-sm-4 col-sm-7">
            <div class="card-notes notification-message pt-1">
              If IC did not provide credits the order charge should be $0.00 Please just waive concierge fee
            </div>
          </div>
        </div>
      </fieldset>
      <div
        v-if="groceryInfo.noGoGoFee"
        class="row align-items-center"
      >
        <label class="col-sm-4 control-label">Reason*</label>
        <div class="col-sm-7">
          <dropdown-list
            v-model="groceryInfo.noGoGoFeeReason"
            :options="noGoGoFeeReasonList"
          />
        </div>
      </div>
      <div
        v-if="groceryInfo.applyDiscountedFee"
        class="row"
      >
        <label class="col-sm-4 control-label pt-10"> Reason* </label>
        <div class="col-sm-7">
          <sanitized-textarea
            id="applyDiscountedFeeReason"
            v-model="groceryInfo.applyDiscountedFeeReason"
            required
            placeholder="Reason for apply discounted gogo fee"
            autosize
          />
        </div>
      </div>
      <div class="row">
        <label class="col-sm-4 control-label pt-10"> Notes </label>
        <div class="col-sm-7">
          <sanitized-textarea
            :id="`notes-${pageId}`"
            v-model="groceryInfo.notes"
            :required="(groceryInfo.noGoGoFee || groceryInfo.doNotCharge) ?? false"
            placeholder="Notes"
            autosize
          />
        </div>
      </div>
    </template>

    <template #submit-button>
      Create Grocery
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch, onBeforeUnmount, onMounted } from 'vue';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { ListType } from '@/models/app';
import { GroceryInput } from '@/models/grocery';
import { yesNoOptions } from '@/constants/app';
import { createGrocery } from '@/services/grocery/createGrocery';

const pageId = 'create-grocery';

const noGoGoFeeReasonList: ListType[] = [
  { id: 1, key: 'Retention Due to IC Dispute', label: 'Retention Due to IC Dispute' },
  { id: 2, key: 'Other Circumstances Issue', label: 'Other Circumstances Issue' },
  { id: 3, key: 'Re-Order', label: 'Re-Order' },
  { id: 4, key: 'Partnership', label: 'Partnership' },
  { id: 5, key: 'Employee', label: 'Employee' },
];

const doNotChargeReasonList: ListType[] = [
  { id: 1, key: 'We received IC Credit for Entire Order Missing', label: 'We received IC Credit for Entire Order Missing' },
  {
    id: 2,
    key: 'We received IC Credit for too many items not shopped by shopper',
    label: 'We received IC Credit for too many items not shopped by shopper',
  },
  {
    id: 3,
    key: 'No IC credit or New Order. We are compensating customer due to GoGo Error',
    label: 'No IC credit or New Order. We are compensating customer due to GoGo Error',
  },
  {
    id: 4,
    key: 'CRM Free Liquids promotion no IC credits where received',
    label: 'CRM Free Liquids promotion no IC credits where received',
  },
];

const { customerPhoneNumber } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { lastFormData, setFormData } = useGlobal(pageId);

type GroceryInfo = GroceryInput & {
  isInstacartProvidedCredits: string | null;
};
const groceryInfo: Ref<GroceryInfo> = ref({
  name: '',
  phoneNumber: '',
  orderId: '',
  email: '',
  noGoGoFee: false,
  isInstacartProvidedCredits: null,
  noGoGoFeeReason: noGoGoFeeReasonList[2].key,
  doNotCharge: false,
  doNotChargeReason: doNotChargeReasonList[0].key,
  applyDiscountedFee: false,
  applyDiscountedFeeReason: '',
  conditionalTip: true,
  notes: '',
});
const isLoading = ref(false);
const isEmailValid = ref(true);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(groceryInfo.value.phoneNumber));
const isDisableName = computed(() => groceryInfo.value.noGoGoFee && groceryInfo.value.noGoGoFeeReason === 'Re-Order');

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const submitForm = async (isConfirmedSameStoreActiveOrder = false): Promise<any> => {
  if (groceryInfo.value.doNotCharge && groceryInfo.value.isInstacartProvidedCredits === 'No') {
    showErrorPopupDialog({
      text: 'If IC did not provide credits the order charge should be $0.00  Please just waive concierge fee',
    });

    return;
  }

  const postData = {
    ...groceryInfo.value,
    doNotChargeReason: groceryInfo.value.doNotCharge ? groceryInfo.value.doNotChargeReason : null,
    noGoGoFeeReason: groceryInfo.value.noGoGoFee ? groceryInfo.value.noGoGoFeeReason : null,
    applyDiscountedFeeReason: groceryInfo.value.applyDiscountedFee ? groceryInfo.value.applyDiscountedFeeReason : null,
    isConfirmedSameStoreActiveOrder,
  };

  isLoading.value = true;
  try {
    await createGrocery(postData);

    showSuccessPopupDialog({
      text: 'The grocery has been created successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);

    if (errorText.includes('This customer has an active order at the same store')) {
      const isSameOrderPlaced = await showConfirmPopupDialog({
        text: `${errorText}<br>Do you still want to submit this order?`,
        title: 'SAME STORE ORDER PLACED',
        confirmButtonText: 'Submit Order',
        cancelButtonText: 'Cancel',
        type: 'html',
        icon: 'error',
        customClass: 'same-store-confirm',
      });

      if (isSameOrderPlaced) {
        return submitForm(true);
      }

      isLoading.value = false;
      return;
    }

    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  groceryInfo.value = {
    name: '',
    phoneNumber: customerPhoneNumber.value || '',
    orderId: '',
    email: '',
    noGoGoFee: false,
    noGoGoFeeReason: noGoGoFeeReasonList[2].key,
    doNotCharge: false,
    isInstacartProvidedCredits: null,
    doNotChargeReason: doNotChargeReasonList[0].key,
    applyDiscountedFee: false,
    applyDiscountedFeeReason: '',
    conditionalTip: true,
    notes: '',
  };
};

watch(customerPhoneNumber, resetForm);
watch(
  () => groceryInfo.value.doNotCharge,
  (value?: boolean | null) => {
    if (value) {
      groceryInfo.value.noGoGoFee = false;
      groceryInfo.value.applyDiscountedFee = false;
    }
  },
);

watch(
  () => groceryInfo.value.noGoGoFeeReason,
  (value?: string | null) => {
    if (value === 'Re-Order' && groceryInfo.value.name !== 'Re-Order') {
      groceryInfo.value.name = 'Re-Order';
    } else if (value !== 'Re-Order' && groceryInfo.value.name === 'Re-Order') {
      groceryInfo.value.name = '';
    }
  },
);

watch(
  () => groceryInfo.value.noGoGoFee,
  (value?: boolean | null) => {
    if (!value && groceryInfo.value.name === 'Re-Order') {
      groceryInfo.value.name = '';
    } else if (value && groceryInfo.value.noGoGoFeeReason === 'Re-Order') {
      groceryInfo.value.name = 'Re-Order';
    }

    if (value) {
      groceryInfo.value.doNotCharge = false;
      groceryInfo.value.applyDiscountedFee = false;
    }
  },
);

watch(
  () => groceryInfo.value.applyDiscountedFee,
  (value?: boolean | null) => {
    if (value) {
      groceryInfo.value.doNotCharge = false;
      groceryInfo.value.noGoGoFee = false;
    }
  },
);

onMounted(() => {
  groceryInfo.value = {
    name: lastFormData.value?.name || '',
    phoneNumber: customerPhoneNumber.value || lastFormData.value?.phoneNumber,
    orderId: lastFormData.value?.orderId || '',
    email: lastFormData.value?.email || '',
    noGoGoFee: lastFormData.value?.noGoGoFee || false,
    noGoGoFeeReason: lastFormData.value?.noGoGoFeeReason || noGoGoFeeReasonList[2].key,
    doNotCharge: lastFormData.value?.doNotCharge || false,
    doNotChargeReason: lastFormData.value?.doNotChargeReason || doNotChargeReasonList[0].key,
    applyDiscountedFee: lastFormData.value?.applyDiscountedFee || false,
    applyDiscountedFeeReason: lastFormData.value?.applyDiscountedFeeReason || '',
    conditionalTip: lastFormData.value?.conditionalTip ?? true,
    isInstacartProvidedCredits: lastFormData.value?.isInstacartProvidedCredits || null,
    notes: lastFormData.value?.notes || '',
  };
});

onBeforeUnmount(() => {
  setFormData(groceryInfo.value);
});
</script>
